import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
	FileData,
	MediaFilesId,
	MediaResponse,
	NavBreadcrumb
} from '@models/models';
import { BrandsServiceProvider } from '@services/brands/brands.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
	BRAND_EDIT_FORM,
	BrandDetailsInfo,
	BrandGDPR,
	BrandInputItem,
	BrandsDetails,
	GetEditMediaResponse,
	UpdateEditMediaResponse
} from '@models/brands.model';
import { SNModalService } from '@web-services/modal/snModal.service';
import { GdprDisableModalComponent } from '@web-components/brands/modals/gdpr-disable-modal/gdpr-disable-modal';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';
import { Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { MessageService } from '@services/helpers/message-service/message.service';
import { SettingsOverviewDataProvider } from '@web-services/settings/settings-overview/settings-overview.data.service';

@Component({
	selector: 'edit-company-section',
	template: require('./edit-company-section.html'),
	styles: [require('./edit-company-section.scss')]
})
export class EditCompanySectionComponent implements OnInit, OnDestroy {
	@Input() companyId: number;

	breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: '/settings/companies', title: 'Companies' },
		{ link: null, title: 'Edit Company' }
	];

	brandDetails: BrandsDetails;
	editBrandsInput: Array<BrandInputItem> = JSON.parse(
		JSON.stringify(BRAND_EDIT_FORM)
	);
	editCompanyForm: FormGroup;
	logoImage: File;
	bannerImage: File;
	gdprChecked = false;
	mediaIds = MediaFilesId;
	btnDisable: boolean;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private brandsService: BrandsServiceProvider,
		private brandsDataService: BrandsDataServiceProvider,
		private snModalService: SNModalService,
		private fb: FormBuilder,
		private messageService: MessageService,
		private settingsOverviewService: SettingsOverviewDataProvider
	) {}

	ngOnInit(): void {
		this.getBrandInfo();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	onGdprChanged(value: boolean): void {
		if (!value) {
			this.snModalService
				.openModal(GdprDisableModalComponent)
				.result.then((disableAnyway: boolean): void => {
					if (disableAnyway) {
						this.gdprChecked = !disableAnyway;
						this.updateGdprState(
							this.brandDetails.gdprFeatures.actions.TURN_OFF_GDPR_FEATURES.url,
							!disableAnyway
						);
					}
				})
				.catch();
		} else {
			this.gdprChecked = value;
			this.updateGdprState(
				this.brandDetails.gdprFeatures.actions.TURN_ON_GDPR_FEATURES.url,
				value
			);
		}
	}

	saveMedia(data: MediaResponse): void {
		if (data.id === this.mediaIds.CompanyLogo) {
			this.updateMedia(
				this.brandDetails.logo.actions.UPDATE_BRAND_LOGO.url,
				data
			);
		}

		if (data.id === this.mediaIds.CompanyBanner) {
			this.updateMedia(
				this.brandDetails.clientPortalBanner.actions.UPDATE_BRAND_BANNER.url,
				data
			);
		}
	}

	saveCompanyInfo(): void {
		if (this.editCompanyForm.valid) {
			this.btnDisable = true;
			this.brandsDataService
				.saveBrandDetailsInfo(
					this.brandDetails.brandInfo.actions.UPDATE_BRAND_DATA.url,
					this.editCompanyForm.value
				)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize((): boolean => (this.btnDisable = false))
				)
				.subscribe((): void => {
					this.messageService.showToaster('s_0', 'companyInformation');
					this.brandsDataService.getBrandsData();
					this.settingsOverviewService.getSettingsOverviewItems().subscribe();
				});
		} else {
			this.brandsService.validateBrandFields(this.editCompanyForm.controls);
		}
	}

	private getBrandInfo(): void {
		this.brandsDataService
			.getBrandDetailsInfo(this.companyId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: BrandsDetails): void => {
				if (!data) {
					return;
				}
				this.brandDetails = data;
				this.gdprChecked = this.brandDetails.gdprFeatures.enabled;
				this.createFormGroup(data.brandInfo);
				if (data.logo.actions.PREVIEW_BRAND_LOGO) {
					this.getEditMedia(
						'PREVIEW_BRAND_LOGO',
						data.logo.actions.PREVIEW_BRAND_LOGO.url
					);
				}
				if (data.clientPortalBanner.actions.PREVIEW_BRAND_BANNER) {
					this.getEditMedia(
						'PREVIEW_BRAND_BANNER',
						data.clientPortalBanner.actions.PREVIEW_BRAND_BANNER.url
					);
				}
			});
	}

	private createFormGroup(data: BrandDetailsInfo): void {
		this.editCompanyForm = this.fb.group(
			this.brandsService.createFormGroup(BRAND_EDIT_FORM, data)
		);
	}

	private updateMedia(postUrl: string, data: MediaResponse): void {
		this.brandsDataService
			.updateMedia(postUrl, data.data)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(response: UpdateEditMediaResponse) => {
					if (response.data.actions.hasOwnProperty('PREVIEW_BRAND_LOGO')) {
						this.getEditMedia(
							'PREVIEW_BRAND_LOGO',
							response.data.actions.PREVIEW_BRAND_LOGO.url
						);
						this.brandDetails.logo.actions = response.data.actions;
					}

					if (response.data.actions.hasOwnProperty('PREVIEW_BRAND_BANNER')) {
						this.getEditMedia(
							'PREVIEW_BRAND_BANNER',
							response.data.actions.PREVIEW_BRAND_BANNER.url
						);
						this.brandDetails.clientPortalBanner.actions =
							response.data.actions;
					}

					this.brandsDataService.updateBrands().subscribe();
					this.settingsOverviewService.getSettingsOverviewItems().subscribe();
				},
				error =>
					this.messageService.showErrorFromBackend(
						error.error.errorTitle,
						error.error.errorMessage
					)
			);
	}

	private getEditMedia(actionName: string, url: string): void {
		this.brandsDataService
			.getEditMedia(url)
			.subscribe((data: GetEditMediaResponse): void => {
				if (actionName === 'PREVIEW_BRAND_LOGO') {
					this.logoImage = this.restoreFileFromBase64(data.data);
				}

				if (actionName === 'PREVIEW_BRAND_BANNER') {
					this.bannerImage = this.restoreFileFromBase64(data.data);
				}
			});
	}

	private deleteMedia(event: string) {
		if (event === this.mediaIds.CompanyLogo) {
			this.brandsDataService
				.deleteMedia(this.brandDetails.logo.actions.DELETE_BRAND_LOGO.url)
				.subscribe(() => {
					this.brandsDataService.updateBrands().subscribe();
					this.settingsOverviewService.getSettingsOverviewItems().subscribe();
				});
		}

		if (event === this.mediaIds.CompanyBanner) {
			this.brandsDataService
				.deleteMedia(
					this.brandDetails.clientPortalBanner.actions.DELETE_BRAND_BANNER.url
				)
				.subscribe();
		}
	}

	private restoreFileFromBase64(fileData: FileData): File {
		const imageBlob = this.dataURItoBlob(fileData.base64);
		return new File([imageBlob], fileData.name, { type: fileData.contentType });
	}

	private dataURItoBlob(dataURI) {
		const byteString = window.atob(dataURI);
		const arrayBuffer = new ArrayBuffer(byteString.length);
		const int8Array = new Uint8Array(arrayBuffer);
		for (let i = 0; i < byteString.length; i++) {
			int8Array[i] = byteString.charCodeAt(i);
		}
		const blob = new Blob([int8Array], { type: 'image/jpeg' });
		return blob;
	}

	private updateGdprState(url: string, enabled: boolean): void {
		this.brandsDataService
			.changeGdprState(url, { enabled })
			.pipe(
				takeUntil(this.unsubscribe),
				catchError(() => of((this.gdprChecked = !this.gdprChecked)))
			)
			.subscribe((data: BrandGDPR): void => {
				this.brandDetails.gdprFeatures = data;
			});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/settings/edit-company-section/edit-company-section.ts